import React from "react";
import { Col, Form } from "react-bootstrap";
import Datetime from "react-datetime";
import * as Yup from "yup";
import { addHours, endOfDay, isAfter, isBefore, isEqual, isValid, parse, startOfDay } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

export const ACTIVITY_AVAILABILITY_TIMEZONE = "America/Sao_Paulo";
const DISPLAY_FORMAT = "dd/MM/yyyy HH:mm";
const EVENT_DATE_FORMAT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;

export const createActivityAvailability = () => ({
  status: false,
  start_at: null,
  end_at: null,
});

const parseDisplayDate = (value) => {
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = parse(value.trim(), DISPLAY_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

const parseEventDate = (value) => {
  const displayDate = parseDisplayDate(value);
  if (displayDate) return displayDate;
  if (!value) return null;
  const normalizedValue = String(value).trim();
  const date = EVENT_DATE_FORMAT.test(normalizedValue)
    ? new Date(`${normalizedValue.replace(" ", "T")}:00.000Z`)
    : new Date(value);
  return isValid(date) ? date : null;
};

const formatAvailabilityDateForForm = (value) => {
  if (!value) return "";
  if (parseDisplayDate(value)) return value;
  const date = new Date(value);
  if (!isValid(date)) return "";
  return formatInTimeZone(date, ACTIVITY_AVAILABILITY_TIMEZONE, DISPLAY_FORMAT);
};

const parseActivityJson = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const isActivityAvailabilityConfigured = (value) => (
  value === true || value === 1 || value === "1"
  || (typeof value === "string" && value.trim().toLowerCase() === "true")
);

export const createDefaultActivityAvailabilityDates = (eventEnd, now = new Date(), eventStart = null) => {
  const eventStartDate = parseEventDate(eventStart);
  const eventEndDate = parseEventDate(eventEnd);
  let startDate = addHours(now, 1);

  if (eventStartDate && isBefore(startDate, eventStartDate)) {
    startDate = eventStartDate;
  }
  if (eventEndDate && !isBefore(startDate, eventEndDate)) {
    startDate = eventStartDate && isBefore(eventStartDate, eventEndDate) ? eventStartDate : eventEndDate;
  }

  return {
    start_at: formatInTimeZone(startDate, ACTIVITY_AVAILABILITY_TIMEZONE, DISPLAY_FORMAT),
    end_at: formatAvailabilityDateForForm(eventEndDate || eventEnd),
  };
};

export const formatActivityAvailabilityForForm = (json = {}) => {
  const parsedJson = parseActivityJson(json);
  const availability = parsedJson.availability || {};
  return {
    ...parsedJson,
    availability: {
      status: isActivityAvailabilityConfigured(availability.status),
      start_at: formatAvailabilityDateForForm(availability.start_at),
      end_at: formatAvailabilityDateForForm(availability.end_at),
    },
  };
};

export const normalizeActivityAvailabilityForSubmit = (json = {}) => {
  const enabled = json?.availability?.status === true;
  return {
    ...json,
    availability: {
      status: enabled,
      start_at: enabled ? (json?.availability?.start_at || null) : null,
      end_at: enabled ? (json?.availability?.end_at || null) : null,
    },
  };
};

const isWithinInclusiveRange = (value, minimum, maximum) => (
  (!minimum || isAfter(value, minimum) || isEqual(value, minimum))
  && (!maximum || isBefore(value, maximum) || isEqual(value, maximum))
);

export const activityAvailabilityValidationSchema = (dataTable = {}) => Yup.object().shape({
  status: Yup.boolean().required(),
  start_at: Yup.string().nullable().when("status", {
    is: true,
    then: Yup.string()
      .required("Campo obrigatório")
      .test("valid-activity-start", "Data inválida", (value) => Boolean(parseDisplayDate(value)))
      .test("activity-start-in-event", "A data deve estar dentro do período do evento", (value) => {
        const current = parseDisplayDate(value);
        if (!current) return true;
        return isWithinInclusiveRange(
          current,
          parseEventDate(dataTable?.creation_date),
          parseEventDate(dataTable?.end_date),
        );
      }),
    otherwise: Yup.string().nullable(),
  }),
  end_at: Yup.string().nullable().when("status", {
    is: true,
    then: Yup.string()
      .required("Campo obrigatório")
      .test("valid-activity-end", "Data inválida", (value) => Boolean(parseDisplayDate(value)))
      .test("activity-end-in-event", "A data deve estar dentro do período do evento", (value) => {
        const current = parseDisplayDate(value);
        if (!current) return true;
        return isWithinInclusiveRange(
          current,
          parseEventDate(dataTable?.creation_date),
          parseEventDate(dataTable?.end_date),
        );
      })
      .test("activity-end-after-start", "A data de término deve ser posterior à data de início", function (value) {
        const start = parseDisplayDate(this.parent?.start_at);
        const end = parseDisplayDate(value);
        if (!start || !end) return true;
        return isAfter(end, start);
      }),
    otherwise: Yup.string().nullable(),
  }),
}).default(createActivityAvailability());

const getInputClassName = (error, touched) => (
  `form-control ${error ? "is-invalid" : touched ? "is-valid" : ""}`.trim()
);

const buildDatePredicate = ({ minimum, maximum }) => (current) => {
  const date = current.toDate();
  return isWithinInclusiveRange(
    date,
    minimum ? startOfDay(minimum) : null,
    maximum ? endOfDay(maximum) : null,
  );
};

export const ActivityAvailabilityFields = ({ values, errors = {}, touched = {}, setFieldValue, dataTable }) => {
  const availability = values?.json?.availability || createActivityAvailability();
  const availabilityErrors = errors?.json?.availability || {};
  const availabilityTouched = touched?.json?.availability || {};
  const eventStart = parseEventDate(dataTable?.creation_date);
  const eventEnd = parseEventDate(dataTable?.end_date);

  const updateDate = (field, value) => {
    setFieldValue(
      `json.availability.${field}`,
      value && typeof value.format === "function" ? value.format("DD/MM/YYYY HH:mm") : value || "",
    );
  };

  return (
    <>
      <Form.Group as={Col} md={12}>
        <Form.Check
          type="checkbox"
          label="Definir período de disponibilidade"
          checked={availability.status === true}
          onChange={(event) => {
            const enabled = event.target.checked;
            setFieldValue("json.availability.status", enabled);
            if (enabled) {
              const defaults = createDefaultActivityAvailabilityDates(dataTable?.end_date, new Date(), dataTable?.creation_date);
              setFieldValue("json.availability.start_at", defaults.start_at);
              setFieldValue("json.availability.end_at", defaults.end_at);
            } else {
              setFieldValue("json.availability.start_at", null);
              setFieldValue("json.availability.end_at", null);
            }
          }}
        />
      </Form.Group>
      {availability.status === true && (
      <div className="row">
          <Form.Group as={Col} md={6} className="mb-3">
            <Form.Label>Início</Form.Label>
            <Datetime
              value={availability.start_at || ""}
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              closeOnSelect
              onChange={(value) => updateDate("start_at", value)}
              isValidDate={buildDatePredicate({ minimum: eventStart, maximum: eventEnd })}
              inputProps={{
                placeholder: "DD/MM/AAAA HH:mm",
                className: getInputClassName(availabilityErrors.start_at, availabilityTouched.start_at),
              }}
            />
            {availabilityErrors.start_at && <div className="invalid-feedback d-block">{availabilityErrors.start_at}</div>}
          </Form.Group>
          <Form.Group as={Col} md={6} className="mb-3">
            <Form.Label>Término</Form.Label>
            <Datetime
              value={availability.end_at || ""}
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              closeOnSelect
              onChange={(value) => updateDate("end_at", value)}
              isValidDate={buildDatePredicate({ minimum: eventStart, maximum: eventEnd })}
              inputProps={{
                placeholder: "DD/MM/AAAA HH:mm",
                className: getInputClassName(availabilityErrors.end_at, availabilityTouched.end_at),
              }}
            />
            {availabilityErrors.end_at && <div className="invalid-feedback d-block">{availabilityErrors.end_at}</div>}
          </Form.Group>
      </div>
      )}
    </>
  );
};
